/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import axios from 'axios';
import jwt_decode from "jwt-decode";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import Loader from "../Loader";
import { useNavigate, Link } from 'react-router-dom';

// Company id dedicated to ENRMS quick runs. Must match QUICK_RUN_CID in
// backend/controllers/Enrms/batch.js and caller_dailing/schedulerUtils.js.
const QUICK_RUN_CID = 194;

const EnrmsQuickRun = () => {
    const [name, setName] = useState('');
    const [token, setToken] = useState('');
    const [expire, setExpire] = useState('');
    const [role, setRole] = useState('');
    const [access, setAccess] = useState([]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [numbers, setNumbers] = useState('');
    const [validationError, setValidationError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [groupList, setGroupList] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [companyList, setCompanyList] = useState([]);
    const [techPrefixCid, setTechPrefixCid] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        refreshToken();
        getUserAccess();
    }, []);

    useEffect(() => {
        if (role > 2) {
            checkAccess();
        }
    }, [role]);

    useEffect(() => {
        if (token) {
            getGroupList();
            getCompanyList();
        }
    }, [token]);

    const refreshToken = async () => {
        try {
            const response = await axios.get(`${baseUrl}/token`);
            setToken(response.data.accessToken);
            const decoded = jwt_decode(response.data.accessToken);
            setName(decoded.name);
            setRole(decoded.role);
            setExpire(decoded.exp);
        } catch (error) {
            if (error.response) {
                navigate("/");
                window.location.reload();
            }
        }
    };

    const getUserAccess = async () => {
        try {
            const response = await axiosJWT.get(`${baseUrl}/user-access`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setAccess(response.data.results);
        } catch (error) {
            console.log(error);
        }
    };

    const checkAccess = async () => {
        navigate('/dashboard');
    };

    const axiosJWT = axios.create();

    axiosJWT.interceptors.request.use(async (config) => {
        const currentDate = new Date();
        if (expire * 1000 < currentDate.getTime()) {
            const response = await axios.get(`${baseUrl}/token`);
            config.headers.Authorization = `Bearer ${response.data.accessToken}`;
            setToken(response.data.accessToken);
            const decoded = jwt_decode(response.data.accessToken);
            setName(decoded.name);
            setRole(decoded.role);
            setExpire(decoded.exp);
        }
        return config;
    }, (error) => {
        return Promise.reject(error);
    });

    const getGroupList = async () => {
        try {
            const response = await axiosJWT.get(`${baseUrl}/get-all-group-management-list`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setGroupList(response.data.data);
        } catch (error) {
            console.log(error);
        }
    };

    const getCompanyList = async () => {
        try {
            const response = await axiosJWT.get(`${baseUrl}/active-user-company`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCompanyList(response.data.filter(item => item.id !== 1));
        } catch (error) {
            console.log(error);
        }
    };

    const validateNumbers = (input) => {
        if (!input.trim()) {
            return { valid: false, error: 'Please enter at least one phone number.' };
        }

        const numberArray = input.split(',').map(num => num.trim()).filter(num => num !== '');

        if (numberArray.length === 0) {
            return { valid: false, error: 'Please enter at least one phone number.' };
        }

        if (numberArray.length > 100) {
            return { valid: false, error: 'Maximum 100 numbers allowed.' };
        }

        const phoneRegex = /^\d{10}$/;
        const invalidNumbers = [];

        for (const num of numberArray) {
            if (!phoneRegex.test(num)) {
                invalidNumbers.push(num);
            }
        }

        if (invalidNumbers.length > 0) {
            return {
                valid: false,
                error: `Invalid phone number(s): ${invalidNumbers.join(', ')}. Each number must be exactly 10 digits.`
            };
        }

        const uniqueNumbers = [...new Set(numberArray)];
        if (uniqueNumbers.length !== numberArray.length) {
            return { valid: false, error: 'Duplicate numbers found. Please enter unique numbers only.' };
        }

        return { valid: true, numbers: numberArray };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setValidationError('');
        setSuccessMessage('');

        if (!selectedGroupId) {
            setValidationError('Please select a device group.');
            return;
        }

        const validation = validateNumbers(numbers);

        if (!validation.valid) {
            setValidationError(validation.error);
            return;
        }

        // Format numbers as objects with the quick-run cid
        const formattedNumbers = validation.numbers.map(tn_number => ({
            tn_number: tn_number,
            do_not_originate: 2,
            tags_id: null,
            cid: QUICK_RUN_CID
        }));

        console.log('Validated numbers:', formattedNumbers);
        setButtonDisabled(true);
        setPopupOpen(true);

        try {
            await axiosJWT.post(`${baseUrl}/enrms-quick-run`, {
                cid: QUICK_RUN_CID,
                group_id: selectedGroupId,
                numbers: formattedNumbers,
                ...(techPrefixCid && { tech_prefix_cid: techPrefixCid })
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setSuccessMessage('Numbers submitted successfully! A new batch is running under the BBD ENRMS Quick Run Results account. You can check the results in the Publish Management page under BBD ENRMS Quick Run Results.');
            setNumbers('');
        } catch (error) {
            console.log(error);
            setValidationError(error.response?.data?.message || 'An error occurred while submitting numbers.');
        } finally {
            setButtonDisabled(false);
            setPopupOpen(false);
        }
    };

    return (
        <div className="panelbox">
            <Navbar token={token} access={access} />
            <div className="right-panel">
                <Header name={name} />
                {popupOpen && <Loader />}
                <div className="content-page">
                    <div className="row">
                        <div className="col-8">
                            <div className="left-title">
                                <h1>E-NRMS Quick Run</h1>
                            </div>
                        </div>
                        <div className="col-4 top-right-buttons">
                            <div className="export-btn addnew-button">
                                <Link to="/enrms-management"><span>&larr;</span> Back</Link>
                            </div>
                        </div>
                    </div>

                    <div className="common-white-shadow-background setting-section">
                        <div className="row">
                            <div className="col-6">
                                <div className="settingbox">
                                    <label>Device Group</label>
                                    <div className="cia-wrap">
                                        <select
                                            name="group_id"
                                            id="group_id"
                                            value={selectedGroupId}
                                            onChange={(e) => setSelectedGroupId(e.target.value)}
                                        >
                                            <option value="">Select Group</option>
                                            {groupList.map((group) => (
                                                <option key={group.id} value={group.id}>
                                                    {group.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <div className="settingbox">
                                    <label>Tech Prefix Customer</label>
                                    <div className="cia-wrap">
                                        <select
                                            value={techPrefixCid}
                                            onChange={(e) => setTechPrefixCid(e.target.value)}
                                        >
                                            <option value="">Select Customer (No Tech Prefix)</option>
                                            {companyList.map((company) => (
                                                <option key={company.id} value={company.id}>
                                                    {company.cname}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row" style={{ marginTop: '20px' }}>
                            <h3>Enter Phone Numbers</h3>
                            <p style={{ color: '#666', marginBottom: '15px' }}>
                                Enter up to 100 phone numbers, separated by commas. Each number must be exactly 100 digits.
                            </p>
                            <div className="inputbox">
                                <textarea
                                    value={numbers}
                                    onChange={(e) => setNumbers(e.target.value)}
                                    placeholder="e.g., 1234567890, 0987654321, 5551234567"
                                    rows="4"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        border: '1px solid #ccc',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                            {validationError && (
                                <p className="error" style={{ color: 'red', marginTop: '10px' }}>
                                    {validationError}
                                </p>
                            )}
                            {successMessage && (
                                <p style={{ color: 'green', marginTop: '10px' }}>
                                    {successMessage}{' '}
                                    <Link to="/enrms-publish-management" style={{ color: 'green', textDecoration: 'underline' }}>
                                        Go to Publish Management
                                    </Link>
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="row footer-row">
                        <a
                            href="#"
                            className={buttonDisabled ? 'next disabled-link' : 'next'}
                            onClick={handleSubmit}
                        >
                            Submit
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnrmsQuickRun;
